import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { AlertTriangle, Download, FileText, MessageCircle, RefreshCw } from 'lucide-react';
import Sidebar from '../../components/Sidebar';
import Navbar from '../../components/Navbar';
import api, { BACKEND_URL } from '../../lib/api';

const fmtRs = (v) =>
  v > 0
    ? 'Rs. ' + Number(v).toLocaleString('en-PK', { minimumFractionDigits: 2, maximumFractionDigits: 2 })
    : '—';

const fmtRsBold = (v) =>
  'Rs. ' + Number(v || 0).toLocaleString('en-PK', { minimumFractionDigits: 2, maximumFractionDigits: 2 });

const fmtDate = (d) => {
  if (!d) return '—';
  const dt = new Date(d);
  return `${String(dt.getDate()).padStart(2, '0')}/${String(dt.getMonth() + 1).padStart(2, '0')}/${dt.getFullYear()}`;
};

const today = () => new Date().toISOString().split('T')[0];

const waLink = (phone, name, amount) => {
  const clean = (phone || '').replace(/\D/g, '');
  const num = clean.startsWith('0') ? '92' + clean.slice(1) : clean;
  const msg = `Assalam+o+Alaikum+${encodeURIComponent(name)}+bhai%2C+aapka+Rs.+${encodeURIComponent(
    Number(amount).toLocaleString('en-PK', { minimumFractionDigits: 0 })
  )}+outstanding+hai.+Kindly+payment+karein.+Shukriya+%E2%80%94+SAMS+ERP`;
  return `https://wa.me/${num}?text=${msg}`;
};

const rowBg = (row) => {
  if (row.critical > 0) return { row: 'bg-red-50 hover:bg-red-100', border: 'border-red-100' };
  if (row.late > 0)     return { row: 'bg-orange-50 hover:bg-orange-100', border: 'border-orange-100' };
  if (row.overdue > 0)  return { row: 'bg-amber-50 hover:bg-amber-100', border: 'border-amber-100' };
  return { row: 'bg-blue-50 hover:bg-blue-100', border: 'border-blue-100' };
};

const AgingReport = () => {
  const navigate = useNavigate();
  const [user, setUser] = useState(null);
  const [isSidebarOpen, setIsSidebarOpen] = useState(true);

  const [asOfDate, setAsOfDate] = useState(today());
  const [customerFilter, setCustomerFilter] = useState('');
  const [customerList, setCustomerList] = useState([]);

  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const token = localStorage.getItem('token');
    const u = localStorage.getItem('user');
    if (!token || !u) { navigate('/login'); return; }
    setUser(JSON.parse(u));
    fetchReport();
  }, [navigate]);

  const fetchReport = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({ as_of_date: asOfDate });
      if (customerFilter) params.set('customer_id', customerFilter);
      const r = await api.get(`/reports/aging?${params}`);
      setData(r.data);
      if (r.data.customer_list) setCustomerList(r.data.customer_list);
    } catch (e) {
      console.error(e);
    }
    setLoading(false);
  }, [asOfDate, customerFilter]);

  useEffect(() => { fetchReport(); }, [fetchReport]);

  const exportPdf = () => {
    const token = localStorage.getItem('token');
    const params = new URLSearchParams({ token, as_of_date: asOfDate });
    if (customerFilter) params.set('customer_id', customerFilter);
    window.open(`${BACKEND_URL}/api/reports/aging/export-pdf?${params}`, '_blank');
  };

  const exportCsv = () => {
    const token = localStorage.getItem('token');
    const params = new URLSearchParams({ token, as_of_date: asOfDate });
    if (customerFilter) params.set('customer_id', customerFilter);
    window.open(`${BACKEND_URL}/api/reports/aging/export-csv?${params}`, '_blank');
  };

  const summary = data?.summary || {};
  const customers = data?.customers || [];

  const totals = {
    current: customers.reduce((s, r) => s + r.current, 0),
    overdue: customers.reduce((s, r) => s + r.overdue, 0),
    late: customers.reduce((s, r) => s + r.late, 0),
    critical: customers.reduce((s, r) => s + r.critical, 0),
    total_outstanding: customers.reduce((s, r) => s + r.total_outstanding, 0),
  };

  return (
    <div className="flex h-screen bg-[#f0f2f5]">
      <Sidebar isOpen={isSidebarOpen} toggleSidebar={() => setIsSidebarOpen(!isSidebarOpen)} />

      <div className={`flex-1 flex flex-col min-w-0 transition-all duration-300 ${isSidebarOpen ? 'md:ml-64' : 'md:ml-0'}`}>
        <Navbar user={user} toggleSidebar={() => setIsSidebarOpen(!isSidebarOpen)} />

        <main className="flex-1 overflow-y-auto p-4 sm:p-6 lg:p-8">

          {/* Title + export buttons */}
          <div className="mb-6 flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
            <div>
              <h1 className="text-2xl sm:text-3xl lg:text-4xl font-semibold tracking-tight text-[#1a1a2e]">
                Customer Aging Report
              </h1>
              <p className="text-sm text-[#6b7280] mt-1">
                Who owes you money and how long it's been outstanding
              </p>
            </div>
            <div className="flex gap-2">
              <button
                onClick={exportPdf}
                className="flex items-center gap-2 border border-[#e5e7eb] bg-white text-[#1a1a2e] hover:bg-[#f9fafb] px-3 py-2 rounded-sm text-sm font-medium transition-colors"
              >
                <FileText className="w-4 h-4 text-[#1E40AF]" />
                Export PDF
              </button>
              <button
                onClick={exportCsv}
                className="flex items-center gap-2 border border-[#e5e7eb] bg-white text-[#1a1a2e] hover:bg-[#f9fafb] px-3 py-2 rounded-sm text-sm font-medium transition-colors"
              >
                <Download className="w-4 h-4 text-[#1E40AF]" />
                Export CSV
              </button>
            </div>
          </div>

          {/* Filters */}
          <div className="bg-white border border-[#e5e7eb] rounded-2xl shadow-sm p-4 mb-6 grid grid-cols-1 sm:grid-cols-3 lg:grid-cols-4 gap-4 items-end">
            <div>
              <label className="block text-xs font-medium text-[#6b7280] mb-1">As of Date</label>
              <input
                type="date"
                value={asOfDate}
                onChange={(e) => setAsOfDate(e.target.value)}
                className="w-full border border-[#e5e7eb] rounded-sm px-3 py-2 text-sm focus:outline-none focus:border-[#1E40AF]"
              />
            </div>
            <div>
              <label className="block text-xs font-medium text-[#6b7280] mb-1">Customer</label>
              <select
                value={customerFilter}
                onChange={(e) => setCustomerFilter(e.target.value)}
                className="w-full border border-[#e5e7eb] rounded-sm px-3 py-2 text-sm focus:outline-none focus:border-[#1E40AF]"
              >
                <option value="">All Customers</option>
                {customerList.map((c) => (
                  <option key={c.id} value={c.id}>{c.name}</option>
                ))}
              </select>
            </div>
            <button
              onClick={fetchReport}
              className="flex items-center gap-1.5 border border-[#e5e7eb] rounded-sm px-3 py-2 text-sm text-[#6b7280] hover:bg-[#f9fafb] transition-colors"
            >
              <RefreshCw className="w-3.5 h-3.5" />
              Refresh
            </button>
          </div>

          {/* Summary cards */}
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
            <SummaryCard
              label="Current (0–30 days)"
              value={summary.total_current || 0}
              bg="bg-blue-50" border="border-blue-200"
              text="text-blue-700" dot="bg-blue-500"
            />
            <SummaryCard
              label="Overdue (31–60 days)"
              value={summary.total_overdue || 0}
              bg="bg-amber-50" border="border-amber-200"
              text="text-amber-700" dot="bg-amber-500"
            />
            <SummaryCard
              label="Late (61–90 days)"
              value={summary.total_late || 0}
              bg="bg-orange-50" border="border-orange-200"
              text="text-orange-700" dot="bg-orange-500"
            />
            <SummaryCard
              label="Critical (90+ days)"
              value={summary.total_critical || 0}
              bg="bg-red-50" border="border-red-200"
              text="text-red-700" dot="bg-red-500"
              icon={summary.total_critical > 0 ? <AlertTriangle className="w-4 h-4 text-red-600" /> : null}
            />
          </div>

          {/* Main table */}
          {loading ? (
            <div className="py-16 text-center text-[#6b7280]">Loading aging data…</div>
          ) : customers.length === 0 ? (
            <div className="bg-blue-50 border border-blue-200 rounded-sm p-8 text-center">
              <p className="text-blue-700 font-semibold text-lg">✅ No outstanding receivables found.</p>
              <p className="text-blue-600 text-sm mt-1">All invoices are either paid or there are no confirmed invoices.</p>
            </div>
          ) : (
            <div className="bg-white border border-[#e5e7eb] rounded-2xl shadow-sm overflow-hidden">
              <div className="overflow-x-auto">
                <table className="w-full text-sm min-w-[700px]">
                <thead>
                  <tr className="bg-[#1E40AF] text-white">
                    <th className="py-3 px-4 text-left">Customer</th>
                    <th className="py-3 px-4 text-left">Phone</th>
                    <th className="py-3 px-4 text-right">Current<br /><span className="font-normal text-xs opacity-80">0–30 days</span></th>
                    <th className="py-3 px-4 text-right">Overdue<br /><span className="font-normal text-xs opacity-80">31–60 days</span></th>
                    <th className="py-3 px-4 text-right">Late<br /><span className="font-normal text-xs opacity-80">61–90 days</span></th>
                    <th className="py-3 px-4 text-right">Critical<br /><span className="font-normal text-xs opacity-80">90+ days</span></th>
                    <th className="py-3 px-4 text-right font-semibold">Total</th>
                    <th className="py-3 px-4 text-left">Last Payment</th>
                    <th className="py-3 px-4 text-center">Action</th>
                  </tr>
                </thead>
                <tbody>
                  {customers.map((row) => {
                    const style = rowBg(row);
                    return (
                      <tr key={row.customer_id} className={`${style.row} border-b ${style.border}`}>
                        <td className="py-3 px-4 font-medium text-[#1a1a2e]">
                          {row.critical > 0 && <span className="mr-1">⚠️</span>}
                          {row.customer_name}
                        </td>
                        <td className="py-3 px-4 text-[#6b7280]">{row.phone || '—'}</td>
                        <td className="py-3 px-4 text-right text-blue-700">{fmtRs(row.current)}</td>
                        <td className="py-3 px-4 text-right text-amber-700">{fmtRs(row.overdue)}</td>
                        <td className="py-3 px-4 text-right text-orange-700">{fmtRs(row.late)}</td>
                        <td className="py-3 px-4 text-right text-red-700 font-semibold">{fmtRs(row.critical)}</td>
                        <td className="py-3 px-4 text-right font-bold text-[#1a1a2e]">{fmtRsBold(row.total_outstanding)}</td>
                        <td className="py-3 px-4 text-[#6b7280] text-xs">{fmtDate(row.last_payment_date)}</td>
                        <td className="py-3 px-4 text-center">
                          {row.phone ? (
                            <a
                              href={waLink(row.phone, row.customer_name, row.total_outstanding)}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="inline-flex items-center gap-1 bg-[#25D366] text-white text-xs font-medium px-3 py-1.5 rounded hover:bg-[#1ebe57] transition-colors"
                            >
                              <MessageCircle className="w-3 h-3" />
                              Call
                            </a>
                          ) : (
                            <span className="text-xs text-[#9ca3af]">No phone</span>
                          )}
                        </td>
                      </tr>
                    );
                  })}

                  {/* Totals row */}
                  <tr className="bg-[#F5F5F5] border-t-2 border-[#e5e7eb] font-bold text-[#1a1a2e]">
                    <td className="py-3 px-4" colSpan={2}>TOTAL</td>
                    <td className="py-3 px-4 text-right text-blue-700">{fmtRsBold(totals.current)}</td>
                    <td className="py-3 px-4 text-right text-amber-700">{fmtRsBold(totals.overdue)}</td>
                    <td className="py-3 px-4 text-right text-orange-700">{fmtRsBold(totals.late)}</td>
                    <td className="py-3 px-4 text-right text-red-700">{fmtRsBold(totals.critical)}</td>
                    <td className="py-3 px-4 text-right">{fmtRsBold(totals.total_outstanding)}</td>
                    <td className="py-3 px-4" colSpan={2} />
                  </tr>
                </tbody>
              </table>
            </div>
          </div>
          )}

        </main>
      </div>
    </div>
  );
};

const SummaryCard = ({ label, value, bg, border, text, dot, icon }) => (
  <div className={`${bg} border ${border} rounded-sm p-5`}>
    <div className="flex items-center gap-2 mb-2">
      <span className={`inline-block w-2.5 h-2.5 rounded-full ${dot}`} />
      <span className={`text-xs font-medium uppercase tracking-wide ${text}`}>{label}</span>
      {icon}
    </div>
    <p className={`text-xl font-bold ${text}`}>
      Rs. {Number(value).toLocaleString('en-PK', { minimumFractionDigits: 2 })}
    </p>
  </div>
);

export default AgingReport;
